import axios from 'axios';
import { getApiBaseUrl } from '@/config/runtimeConfig';

export const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Desembrulha StandardResponse do backend (errno/errmsg/data) em respostas 2xx. */
export function unwrapStandardResponsePayload(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }
  if (typeof data.access_token === 'string' && data.user) {
    return data;
  }
  const errno = Number(data.errno);
  const errmsg = String(data.errmsg ?? '').toLowerCase();
  if (errno === 0 && errmsg === 'ok' && data.data !== undefined && data.data !== null) {
    return data.data;
  }
  return data;
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (response?.data) {
      response.data = unwrapStandardResponsePayload(response.data);
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken && !error.config._retry) {
        error.config._retry = true;
        try {
          const { data: refreshData } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const data = unwrapStandardResponsePayload(refreshData);
          localStorage.setItem('access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }
          error.config.headers.Authorization = `Bearer ${data.access_token}`;
          return api.request(error.config);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          const path = window.location.pathname || '';
          if (!path.startsWith('/login') && !path.startsWith('/cadastro')) {
            window.location.assign('/login');
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  adminLogin: (credentials) => api.post('/auth/admin/login', credentials),
  signup: (userData) => api.post('/auth/signup', userData),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  googleSignIn: (token, role = 'client', picture = null) =>
    api.post('/auth/google', {
      token,
      role,
      ...(picture ? { picture } : {}),
    }),
  googleCompleteProfile: (role, referralCode) =>
    api.post('/auth/google/complete-profile', {
      role,
      ...(referralCode ? { referral_code: referralCode } : {}),
    }),
  appleSignIn: (payload) => api.post('/auth/apple', payload),
  appleCompleteProfile: (role, referralCode) =>
    api.post('/auth/apple/complete-profile', {
      role,
      ...(referralCode ? { referral_code: referralCode } : {}),
    }),
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },
};

export const securityAPI = {
  get2FAStatus: () => api.get('/security/2fa/status'),
  setup2FA: () => api.post('/security/2fa/setup'),
  enable2FA: (code) => api.post('/security/2fa/enable', { code }),
  verify2FA: (code) => api.post('/security/2fa/verify', { code }),
  disable2FA: () => api.post('/security/2fa/disable'),
};

export const adminAPI = {
  getMe: () => api.get('/admin/me'),
  getStaff: () => api.get('/admin/staff'),
  createStaff: (body) => api.post('/admin/staff', body),
  patchStaff: (staffId, body) => api.patch(`/admin/staff/${staffId}`, body),
  getStats: () => api.get('/admin/stats'),
  getPlatformOverview: () => api.get('/admin/platform/overview'),
  getPlatformStripeSummary: () => api.get('/admin/platform/stripe/summary'),
  getPlatformAwsCosts: (month) =>
    api.get('/admin/platform/costs/aws', { params: month ? { month } : {} }),
  getPlatformGcpCosts: (month) =>
    api.get('/admin/platform/costs/gcp', { params: month ? { month } : {} }),
  getConfig: () => api.get('/admin/config'),
  getPricingConfig: () => api.get('/admin/pricing-config'),
  updatePricingConfig: (data) => api.put('/admin/pricing-config', data),
  updateConfig: (config) => api.put('/admin/config', config),
  getReferralStats: () => api.get('/admin/referrals/stats'),
  getReferrals: (params) => api.get('/admin/referrals', { params }),
  updateReferralCommission: (id, body) => api.patch(`/admin/referrals/commissions/${id}`, body),
  updateUserReferredBy: (userId, body) => api.patch(`/admin/users/${userId}/referred-by`, body),
  purgeAllJobs: (confirmation) =>
    api.post('/admin/maintenance/purge-all-jobs', { confirmation }),
  getCoverageNeighborhoods: () => api.get('/admin/coverage'),
  patchCoverageNeighborhood: (id, body) =>
    api.patch(`/admin/coverage/neighborhoods/${id}`, body),
  createCoverageNeighborhood: (body) => api.post('/admin/coverage/neighborhoods', body),
  deleteCoverageNeighborhood: (id) => api.delete(`/admin/coverage/neighborhoods/${id}`),
  bulkCoverageRegion: (body) => api.post('/admin/coverage/bulk-region', body),
  seedCoverageDefaults: () => api.post('/admin/coverage/seed-defaults'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserFull: (userId) => api.get(`/admin/users/${userId}/full`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  banUser: (userId, reason = 'Banimento manual pelo admin') =>
    api.put(`/admin/users/${userId}/ban`, { reason }),
  unbanUser: (userId) => api.put(`/admin/users/${userId}/unban`),
  verifyUser: (userId) => api.put(`/admin/users/${userId}/verify`),
  getJobs: (params) => api.get('/admin/jobs', { params }),
  cancelJob: (jobId, body) => api.post(`/admin/jobs/${jobId}/cancel`, body),
  getCancellationRequests: (params) => api.get('/admin/cancellation-requests', { params }),
  resolveCancellationRequest: (requestId, body) =>
    api.post(`/admin/cancellation-requests/${requestId}/resolve`, body),
  getPayments: (params) => api.get('/admin/payments', { params }),
  forceReleasePayment: (paymentId) => api.post(`/admin/payments/${paymentId}/force-release`),
  getModerationLogs: (params) => api.get('/admin/moderation-logs', { params }),
  getEquipmentVerifications: (params) => api.get('/admin/equipment-verifications', { params }),
  decideEquipmentVerification: (userId, equipmentId, body) =>
    api.post(`/admin/equipment-verifications/${userId}/${equipmentId}/decision`, body),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  getGrowthAnalytics: (months = 6) => api.get(`/admin/analytics/growth?months=${months}`),
  getRevenueAnalytics: (months = 6) => api.get(`/admin/analytics/revenue?months=${months}`),
  getConversionAnalytics: () => api.get('/admin/analytics/conversion'),
  getTopPerformers: (limit = 10) => api.get(`/admin/analytics/top-performers?limit=${limit}`),
  getRealTimeAnalytics: () => api.get('/admin/analytics/real-time'),
  getCoupons: (params) => api.get('/financial/coupons', { params }),
  createCoupon: (data) => api.post('/financial/coupons', data),
  updateCoupon: (couponId, data) => api.put(`/financial/coupons/${couponId}`, data),
  deleteCoupon: (couponId) => api.delete(`/financial/coupons/${couponId}`),
  getVideomakerDiscountPermissions: () => api.get('/financial/videomaker-discount-permissions'),
  updateVideomakerDiscountPermission: (videomakerId, data) =>
    api.put(`/financial/videomaker-discount-permissions/${videomakerId}`, data),
  getNotificationStats: () => api.get('/notifications/stats'),
  getNotificationLogs: (limit = 50) => api.get(`/notifications/logs?limit=${limit}`),
  sendBroadcastNotification: (data) => api.post('/notifications/broadcast', data),
  sendNotification: (data) => api.post('/notifications/send', data),
  getAdminNotificationsSummary: (params) => api.get('/admin/notifications/summary', { params }),
  listAdminNotifications: (params) => api.get('/admin/notifications', { params }),
  markAdminNotificationRead: (notificationId) =>
    api.patch(`/admin/notifications/${notificationId}/read`),
  markAllAdminNotificationsRead: () => api.patch('/admin/notifications/read-all'),
  getFinancialReport: (month) =>
    api.get('/financial/admin/financial-report', { params: { month } }),
};

export const usersAPI = {
  getProfile: () => api.get('/users/me'),
  updateProfile: (data) => api.put('/users/me', data),
  getVideomakers: (params) => api.get('/users/videomakers', { params }),
  uploadProfilePicture: (formData) =>
    api.post('/users/profile/picture', formData, { timeout: 60000 }),
  createStripeConnect: () => api.post('/users/videomaker/stripe-onboarding'),
  getStripeDashboard: () => api.get('/users/videomaker/stripe-dashboard'),
  getStripeStatus: () => api.get('/users/videomaker/stripe-status'),
  getStripeBalanceSummary: () => api.get('/users/videomaker/stripe-balance-summary'),
};

export const jobsAPI = {
  create: (jobData) => api.post('/jobs', jobData),
  list: (params) => api.get('/jobs', { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  cancel: (id) => api.delete(`/jobs/${id}`),
};

export const deliveryAPI = {
  create: (data) => api.post('/delivery', data),
  getByJob: (jobId) => api.get(`/delivery/job/${jobId}`),
  completeUpload: (deliveryId, fileData) =>
    api.post(`/delivery/${deliveryId}/upload/complete`, fileData),
  approve: (deliveryId, data) => api.post(`/delivery/${deliveryId}/approve`, data),
  approveFile: (deliveryId, fileId, action, message = null) =>
    api.post(`/delivery/${deliveryId}/file-approval`, { file_id: fileId, action, message }),
  getOriginalDownloadLinks: (jobId) => api.get(`/delivery/${jobId}/download-original`),
  getProgress: (jobId) => api.get(`/delivery/job/${jobId}/progress`),
};

export const financialAPI = {
  getMyTransactions: (limit = 50) =>
    api.get('/financial/transactions/my-history', { params: { limit } }),
  getVideomakerEarnings: (params = 6) => {
    if (typeof params === 'number') {
      return api.get('/financial/videomaker/earnings', { params: { months: params } });
    }
    return api.get('/financial/videomaker/earnings', { params });
  },
};

export const proposalsAPI = {
  create: (data) => api.post('/proposals', data),
  getByJob: (jobId) => api.get(`/proposals/job/${jobId}`),
  accept: (id) => api.put(`/proposals/${id}/accept`),
  reject: (id) => api.put(`/proposals/${id}/reject`),
  getMyProposals: () => api.get('/proposals/my-proposals'),
};

export const paymentsAPI = {
  hold: (data) => api.post('/payments/hold', data),
  release: (id) => api.post(`/payments/${id}/release`),
  refund: (id) => api.post(`/payments/${id}/refund`),
  getStatus: (id) => api.get(`/payments/${id}`),
};

export const ratingsAPI = {
  create: (data) => api.post('/ratings', data),
  getUserRatings: (userId, page = 1, limit = 20) =>
    api.get(`/ratings/user/${userId}`, { params: { page, limit } }),
  checkJobRating: (jobId) => api.get(`/ratings/job/${jobId}`),
  getPending: () => api.get('/ratings/pending'),
};

export default api;
